import { supabase } from "../lib/supabase";

export interface ConversationDB {
  id: string;
  student_id: string;
  title: string;
  created_at: string;
  updated_at: string;
  cleared_question_count?: number;
  cleared_category_breakdown?: Record<string, number>;
}

export interface MessageDB {
  id: string;
  conversation_id: string;
  role: string;
  content: string;
  created_at: string;
  message_type?: string;
}

// ... existing imports ...

/**
 * Update message category
 */
export async function updateMessageCategory(messageId: string, category: string): Promise<void> {
  const { error } = await supabase
    .from("research_messages")
    .update({ message_type: category })
    .eq("id", messageId);

  if (error) {
    console.error("Error updating message category:", error);
  }
}

export interface ResearchStats {
  total: number;
  breakdown: Record<string, number>;
}

/**
 * Get research stats (total valid questions and category breakdown)
 * Includes both current messages AND cleared stats from previous sessions
 */
export async function getResearchStats(studentId: string): Promise<ResearchStats> {
  try {
    // 1. Get all conversations with their cleared stats
    const { data: conversations, error: convError } = await supabase
      .from("research_conversations")
      .select("id, cleared_question_count, cleared_category_breakdown")
      .eq("student_id", studentId);

    if (convError) {
      console.error("Error fetching conversations:", convError);
      return { total: 0, breakdown: {} };
    }

    if (!conversations || conversations.length === 0) {
      return { total: 0, breakdown: {} };
    }

    const conversationIds = conversations.map(c => c.id);

    // 2. Start with cleared stats from all conversations
    let total = 0;
    const breakdown: Record<string, number> = {};

    conversations.forEach(conv => {
      // Add cleared question count
      total += conv.cleared_question_count || 0;

      // Merge cleared category breakdown
      const clearedBreakdown = conv.cleared_category_breakdown || {};
      Object.entries(clearedBreakdown).forEach(([category, count]) => {
        breakdown[category] = (breakdown[category] || 0) + (count as number);
      });
    });

    // 3. Fetch all current user messages with their type
    const { data: messages, error: msgError } = await supabase
      .from("research_messages")
      .select("role, message_type")
      .in("conversation_id", conversationIds)
      .eq("role", "user");

    if (msgError) {
      console.error("Error fetching messages for stats:", msgError);
      // Still return cleared stats even if message fetch fails
      return { total, breakdown };
    }

    // 4. Add current message stats
    (messages || []).forEach(msg => {
      const type = msg.message_type || "Others"; // Default if null

      // Filter out "Others" from the main count as requested
      if (type !== "Others") {
        total++;
        breakdown[type] = (breakdown[type] || 0) + 1;
      }
    });

    return { total, breakdown };

  } catch (e) {
    console.error("Error in getResearchStats:", e);
    return { total: 0, breakdown: {} };
  }
}

/**
 * Get total number of questions (user messages) asked by a student across all conversations
 * Now delegates to getResearchStats to ensure consistency (filtering Others)
 */
export async function getTotalResearchQuestionCount(studentId: string): Promise<number> {
  const stats = await getResearchStats(studentId);
  return stats.total;
}

/**
 * Create a new research conversation
 */
export async function createConversation(
  studentId: string,
  title: string = "Research Session"
): Promise<string> {
  const { data, error } = await supabase
    .from("research_conversations")
    .insert({
      student_id: studentId,
      title,
      updated_at: new Date().toISOString(),
    })
    .select("id")
    .single();

  if (error) {
    console.error("Error creating research conversation:", error);
    throw new Error("Failed to create conversation");
  }

  return data.id;
}

/**
 * Get all conversations for a student
 */
export async function getConversations(
  studentId: string
): Promise<ConversationDB[]> {
  const { data, error } = await supabase
    .from("research_conversations")
    .select("*")
    .eq("student_id", studentId)
    .order("updated_at", { ascending: false });

  if (error) {
    console.error("Error fetching research conversations:", error);
    throw new Error("Failed to fetch conversations");
  }

  return data || [];
}

/**
 * Save a message to a conversation
 */
export async function saveMessage(
  conversationId: string,
  role: "user" | "assistant",
  content: string
): Promise<MessageDB> {
  const { data, error } = await supabase
    .from("research_messages")
    .insert({
      conversation_id: conversationId,
      role,
      content,
    })
    .select("*")
    .single();

  if (error) {
    console.error("Error saving research message:", error);
    throw new Error("Failed to save message");
  }

  // Update conversation's updated_at
  await supabase
    .from("research_conversations")
    .update({ updated_at: new Date().toISOString() })
    .eq("id", conversationId);

  return data;
}

/**
 * Get messages for a conversation with pagination
 */
export async function getMessages(
  conversationId: string,
  limit: number = 100,
  offset: number = 0
): Promise<MessageDB[]> {
  const { data, error } = await supabase
    .from("research_messages")
    .select("*")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error("Error fetching research messages:", error);
    throw new Error("Failed to fetch messages");
  }

  return data || [];
}

/**
 * Get message count for a conversation
 */
export async function getMessageCount(
  conversationId: string
): Promise<number> {
  const { count, error } = await supabase
    .from("research_messages")
    .select("*", { count: "exact", head: true })
    .eq("conversation_id", conversationId);

  if (error) {
    console.error("Error counting research messages:", error);
    return 0;
  }

  return count || 0;
}

/**
 * Delete a conversation and all its messages
 */
export async function deleteConversation(
  conversationId: string
): Promise<void> {
  const { error } = await supabase
    .from("research_conversations")
    .delete()
    .eq("id", conversationId);

  if (error) {
    console.error("Error deleting research conversation:", error);
    throw new Error("Failed to delete conversation");
  }
}

/**
 * Clear all messages from a conversation while preserving stats
 * This saves the current stats to the conversation's cleared_* columns before deleting messages
 */
export async function clearConversationMessages(
  conversationId: string
): Promise<void> {
  try {
    // 1. First, get current stats from messages that will be deleted
    const { data: messages, error: msgError } = await supabase
      .from("research_messages")
      .select("role, message_type")
      .eq("conversation_id", conversationId)
      .eq("role", "user");

    if (msgError) {
      console.error("Error fetching messages before clear:", msgError);
      throw new Error("Failed to get message stats");
    }

    // 2. Calculate stats from current messages
    let currentCount = 0;
    const currentBreakdown: Record<string, number> = {};

    (messages || []).forEach(msg => {
      const type = msg.message_type || "Others";
      if (type !== "Others") {
        currentCount++;
        currentBreakdown[type] = (currentBreakdown[type] || 0) + 1;
      }
    });

    // 3. Get existing cleared stats from the conversation
    const { data: conversation, error: convError } = await supabase
      .from("research_conversations")
      .select("cleared_question_count, cleared_category_breakdown")
      .eq("id", conversationId)
      .single();

    if (convError) {
      console.error("Error fetching conversation:", convError);
      throw new Error("Failed to get conversation");
    }

    // 4. Merge with existing cleared stats
    const existingCount = conversation?.cleared_question_count || 0;
    const existingBreakdown = conversation?.cleared_category_breakdown || {};

    const newCount = existingCount + currentCount;
    const newBreakdown: Record<string, number> = { ...existingBreakdown };

    Object.entries(currentBreakdown).forEach(([category, count]) => {
      newBreakdown[category] = (newBreakdown[category] || 0) + count;
    });

    // 5. Update conversation with merged stats
    const { error: updateError } = await supabase
      .from("research_conversations")
      .update({
        cleared_question_count: newCount,
        cleared_category_breakdown: newBreakdown,
        updated_at: new Date().toISOString(),
      })
      .eq("id", conversationId);

    if (updateError) {
      console.error("Error updating cleared stats:", updateError);
      throw new Error("Failed to save cleared stats");
    }

    // 6. Now delete all messages
    const { error: deleteError } = await supabase
      .from("research_messages")
      .delete()
      .eq("conversation_id", conversationId);

    if (deleteError) {
      console.error("Error deleting messages:", deleteError);
      throw new Error("Failed to delete messages");
    }


  } catch (e) {
    console.error("Error in clearConversationMessages:", e);
    throw e;
  }
}



/**
 * Get daily activity (message count) for the last 7 days.
 * Returns an array of numbers of length 7, where index 6 is today.
 */
export async function getDailyActivityLast7Days(studentId: string): Promise<number[]> {
  try {
    // 1. Calculate the start date (7 days ago)
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today

    const start = new Date(today);
    start.setDate(today.getDate() - 6); // 6 days ago + today = 7 days
    start.setHours(0, 0, 0, 0); // Start of start date

    // 2. Get conversations
    const { data: conversations, error: convError } = await supabase
      .from("research_conversations")
      .select("id")
      .eq("student_id", studentId);

    if (convError || !conversations || conversations.length === 0) {
      return Array(7).fill(0);
    }

    const conversationIds = conversations.map(c => c.id);

    // 3. Fetch user messages created >= start date
    const { data: messages, error: msgError } = await supabase
      .from("research_messages")
      .select("created_at")
      .in("conversation_id", conversationIds)
      .eq("role", "user")
      .gte("created_at", start.toISOString());

    if (msgError) {
      console.error("Error fetching messages for activity:", msgError);
      return Array(7).fill(0);
    }

    // 4. Map messages to the last 7 days
    const buckets = Array(7).fill(0);
    const normalize = (d: Date) => {
      const n = new Date(d);
      n.setHours(0, 0, 0, 0);
      return n.getTime();
    };

    const targets = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      targets.push(normalize(d));
    }

    (messages || []).forEach(msg => {
      const msgDate = new Date(msg.created_at);
      const msgTime = normalize(msgDate);
      const index = targets.indexOf(msgTime);
      if (index !== -1) {
        buckets[index]++;
      }
    });

    return buckets;

  } catch (e) {
    console.error("Error in getDailyActivityLast7Days:", e);
    return Array(7).fill(0);
  }
}
